import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import GetAppIcon from '@mui/icons-material/GetApp';
import api from '../../utils/api';
import { ListPageLayout } from '../../components/ListPage';
import { tokens } from '../../theme/tokens';

export default function EducationExamCandidates() {
  const [candidates, setCandidates] = useState([]);
  const [exams, setExams] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [selectedExam, setSelectedExam] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({ exam_id: '', member_id: '' });

  useEffect(() => {
    fetchExams();
    fetchMembers();
  }, []);

  useEffect(() => {
    if (selectedExam) {
      fetchCandidates();
    }
  }, [page, rowsPerPage, selectedExam]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (page === 0 && selectedExam) {
        fetchCandidates();
      } else if (selectedExam) {
        setPage(0);
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [search]);

  const fetchExams = async () => {
    try {
      const response = await api.get('/education/exams', { params: { limit: 1000 } });
      setExams(response.data.data || []);
    } catch (err) {
      console.error('Error fetching exams:', err);
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await api.get('/members', { params: { limit: 1000, status: 'Active' } });
      setMembers(response.data.data || []);
    } catch (err) {
      console.error('Error fetching members:', err);
    }
  };

  const fetchCandidates = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/education/exam-candidates', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          exam_id: selectedExam,
          search: search || undefined,
        },
      });
      setCandidates(response.data.data || []);
      setTotal(Number(response.data.meta?.total) || 0);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch candidates');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = () => {
    setFormData({ exam_id: selectedExam || '', member_id: '' });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({ exam_id: '', member_id: '' });
  };

  const handleSubmit = async () => {
    try {
      await api.post('/education/exam-candidates', formData);
      handleCloseDialog();
      fetchCandidates();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to register candidate');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to remove this candidate?')) {
      return;
    }
    try {
      await api.delete(`/education/exam-candidates/${id}`);
      fetchCandidates();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove candidate');
    }
  };

  const handleExport = async (format) => {
    try {
      const response = await api.get(`/education/exam-candidates/export/${format}`, {
        params: { exam_id: selectedExam, search: search || undefined },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `exam-candidates.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Failed to export candidates');
    }
  };

  return (
    <>
    <ListPageLayout
      title="Exam Candidates"
      crumbs={
        <>
          Operations / <Box component="b" sx={{ color: tokens.ink, fontWeight: 500 }}>Exam Candidates</Box>
        </>
      }
      searchPlaceholder="Search candidates..."
      searchValue={search}
      onSearchChange={setSearch}
      error={error}
      onErrorClose={() => setError('')}
      filters={
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel>Select Exam</InputLabel>
          <Select
            value={selectedExam}
            onChange={(e) => {
              setSelectedExam(e.target.value);
              setPage(0);
            }}
            label="Select Exam"
          >
            {exams.map((exam) => (
              <MenuItem key={exam.id} value={exam.id}>
                {exam.name} ({exam.year_period || 'N/A'})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      }
      actions={
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
            disabled={!selectedExam}
          >
            Register Candidate
          </Button>
          <Button
            variant="outlined"
            startIcon={<GetAppIcon />}
            onClick={() => handleExport('excel')}
            size="small"
            disabled={!selectedExam}
          >
            Excel
          </Button>
          <Button
            variant="outlined"
            startIcon={<GetAppIcon />}
            onClick={() => handleExport('csv')}
            size="small"
            disabled={!selectedExam}
          >
            CSV
          </Button>
        </Box>
      }
    >
      {!selectedExam ? (
        <Paper sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary" align="center">
            Please select an exam to view candidates.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Member ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {candidates.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                          No records yet
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    candidates.map((candidate) => (
                      <TableRow key={candidate.id} hover>
                        <TableCell>{candidate.member_id}</TableCell>
                        <TableCell>
                          {candidate.first_name} {candidate.last_name}
                        </TableCell>
                        <TableCell>{candidate.email}</TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(candidate.id)}
                            title="Remove"
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </>
          )}
        </TableContainer>
      )}
    </ListPageLayout>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Register Candidate</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
            <InputLabel>Exam</InputLabel>
            <Select
              value={formData.exam_id}
              onChange={(e) => setFormData({ ...formData, exam_id: e.target.value })}
              label="Exam"
            >
              {exams.map((exam) => (
                <MenuItem key={exam.id} value={exam.id}>
                  {exam.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Member</InputLabel>
            <Select
              value={formData.member_id}
              onChange={(e) => setFormData({ ...formData, member_id: e.target.value })}
              label="Member"
            >
              {members.map((member) => (
                <MenuItem key={member.id} value={member.id}>
                  {member.first_name} {member.last_name} ({member.member_id})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.member_id || !formData.exam_id}
          >
            Register
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
